import type { Translations } from '../types';

export const en: Translations = {
  common: {
    appName: 'Recovery Drill',
    loading: 'Loading…',
    back: 'Back',
    done: 'Done',
    continue: 'Continue',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    unlock: 'Unlock',
    lock: 'Lock',
    restore: 'Restore',
    settings: 'Settings',
    readiness: 'Readiness',
    gotIt: 'Got it',
    days: 'days',
    months: 'months',
    tomorrow: 'Tomorrow',
    inDays: (days: number) => `In ${days} days`,
    dueYesterday: 'due yesterday',
    dueToday: 'due today',
    dueTomorrow: 'due tomorrow',
    dueInDays: (days: number) => `due in ${days} days`,
    dueInMonths: (months: number) => `due in ${months} months`,
    daysOverdue: (days: number) => `${days} days overdue`,
    never: 'Never',
  },
  nav: {
    readiness: 'Readiness',
    settings: 'Settings',
    home: 'Home',
    howItWorks: 'How it works',
    whatWeCanSee: 'What we can see',
    openApp: 'Open the app',
    startDrill: 'Start a drill',
  },
  landing: {
    badge: 'Recovery Drill',
    heroTitle: 'Most crypto is not lost to hackers.',
    heroP1:
      'It is lost to a drawer nobody remembers, a house that got moved out of, and a phrase written down once and never looked at again. The seed was fine. The memory of where it went was not.',
    heroP2:
      'Recovery Drill rehearses the one thing nobody practises: finding your backup. And it does it without ever holding the backup.',
    startCta: 'Start a drill',
    continueGoogle: 'Continue with Google',
    signingIn: 'Signing in…',
    emailDisclaimer:
      'Used only to know where to send reminders. Your email never touches the vault.',
    claimsHeading: 'What we can and cannot see',
    claimsSub:
      'These are structural claims, not policy ones. The code that touches your hints is forbidden by a lint rule from importing anything that can reach a network, and the build fails if that rule is removed.',
    claims: [
      {
        title: 'We never see your recovery phrase',
        body: 'There is no field for one. The app actively detects and destroys seed-phrase input, in English and Korean, before it can be saved.',
      },
      {
        title: 'Your hints are encrypted on your device',
        body: 'Locked with a vault key the app generates and we cannot reset. Not because we would not — because there is nothing on our side to reset it against.',
      },
      {
        title: 'Our servers hold a calendar and a score',
        body: 'When your next drill is due, how deep a hint you needed, and whether the backup was still where you left it. Nothing that would help anyone find it.',
      },
    ],
    stepsHeading: 'How a drill goes',
    steps: [
      {
        n: '01',
        title: 'Write hints, not secrets',
        body: 'Five levels, shallowest first. Level one should mean nothing to a stranger. Level five should get you there on your worst day.',
      },
      {
        n: '02',
        title: 'Get asked, on a schedule',
        body: 'A push, an email with no links in it, or a calendar entry that just says “Readiness check”. Whichever survives your next phone.',
      },
      {
        n: '03',
        title: 'Actually go and find it',
        body: 'Read the recovery code off the paper and the drill counts as proof the backup physically exists. That is the part nobody else measures.',
      },
      {
        n: '04',
        title: 'The interval adapts',
        body: 'Find it unaided and the gap stretches by a quarter, up to six months. Need a hint and it snaps back to a month. Fail and it is a week.',
      },
    ],
    paperHeading: 'Two things on one piece of paper',
    paperP1:
      'A six-word vault key and a ten-character recovery code, kept with the backup itself. Together with an encrypted export file — which is useless without them — that is the whole recovery story.',
    paperP2:
      'The vault key is not a wallet recovery phrase. It cannot access any cryptocurrency, restore any wallet, or derive any private key. It exists to unlock the hints on your own device, and nothing else.',
    paperP3:
      'It is drawn from a wordlist with every BIP-39 word removed, so your vault key and your wallet phrase can never share a single word — which is how you tell them apart on the paper, years later.',
    footerNote: 'This app stores hints on your device only. Zero knowledge by design.',
  },
  howItWorks: {
    title: 'How it works',
    subtitle: 'Spaced repetition, applied to a physical object you own and hope never to need.',
    twoClocksHeading: 'Two clocks, not one',
    recallTitle: 'Recall',
    recallBody:
      'Asks whether you still know where it is. Its interval adapts: succeed unaided and it stretches by a quarter each time, up to six months. Need a hint and it comes back to a month. Need a deep one and it is a fortnight. Fail and it is a week.',
    physicalTitle: 'Physical',
    physicalBody:
      'Asks whether it is still there and still readable. That one does not adapt. Every 180 days, regardless of how well you have been doing — because a perfect memory of a corroded plate is not a backup.',
    capNote:
      'If the physical check is overdue, your readiness is capped at 60 no matter how well recall is going. The cap is the honest answer to “am I ready?”',
    scoreHeading: 'What the score is made of',
    scoreHalf: 'Half of it is how deep you had to dig for a hint.',
    scoringRows: [
      ['Found it with no hint', '100'],
      ['Needed hint 1', '85'],
      ['Needed hint 2', '65'],
      ['Needed hint 3', '45'],
      ['Needed hint 4 or 5', '20'],
      ['Could not find it', '0'],
    ],
    scoreQuarter: 'A quarter of it is how hard your evidence was.',
    verificationRows: [
      ['Read the recovery code off the paper', '100'],
      ['Held it, but did not check the code', '70'],
      ['Knew where it was, did not go and look', '35'],
      ['Did not verify', '0'],
    ],
    scoreRemaining:
      'The remaining quarter is how recently you drilled and what condition you reported the backup to be in.',
    hintsCostHeading: 'Why the hints cost something',
    hintsCostBody:
      'Hints 4 and 5 are close enough to the answer that seeing them requires typing your vault key again, and they hide themselves after ten seconds. That is not friction for its own sake. If the deepest hint were one tap away, nobody would ever try to remember, and the score would stop measuring anything.',
    lostDeviceHeading: 'If you lose the device',
    lostDeviceP1:
      'The app makes you download an encrypted export file before you can finish setup, and nags until you have. That file plus your vault key restores everything onto a new phone. Neither is any use without the other, and we hold neither.',
    lostDeviceP2:
      'Browsers do evict local storage, and phones do get lost. The export file is the part of the design that assumes both will happen.',
  },
  privacy: {
    title: 'What we can see',
    subtitle: 'Not a summary of a policy. The actual list, field by field.',
    onDeviceHeading: 'Never leaves your device',
    onDeviceSub:
      'Encrypted with AES-256-GCM under a key derived from your vault key with Argon2id (64 MiB, three passes). Present in your export file only as ciphertext.',
    onDeviceItems: [
      'What you call each backup',
      'What it is written on',
      'All five hint levels',
      'The question, its answers, and which one is right',
      'The exact location',
      'Your vault key (the six words that encrypt the hints)',
      'Your recovery code',
    ],
    onServerHeading: 'Reaches our servers',
    onServerSub:
      'Enough to send a reminder at the right hour and to tell you whether you are ready.',
    onServerItems: [
      'An opaque anchor id',
      'How often you drill it, and when the next one is due',
      'When you last drilled, and when you last physically checked',
      'Whether you succeeded, and how deep a hint you needed (a number, 0 to 5)',
      'How you verified — code, held it, from memory, or not at all',
      'Whether you found it in under five minutes',
      'What condition you reported',
      'Your recall and readiness scores',
      'Your email, timezone, and preferred reminder hour',
      'A hash of your recovery code, and a hash of your calendar feed token',
    ],
    neverSendHeading: 'What we will never send you',
    neverSendP1:
      'We will never ask for your wallet recovery phrase, and there is no field in the app that accepts one — any box that takes words actively detects and destroys a seed phrase before it can be saved.',
    neverSendP2:
      'Our emails contain no links. Not a login link, not an unsubscribe link, not a tracking pixel. If you receive a message claiming to be from us with a link in it, or asking for your wallet recovery phrase, your vault key or your recovery code, it is not from us.',
    neverSendP3:
      'Push notifications say “Recovery drill due” and nothing else. Calendar entries are titled “Readiness check”. Both are things other people see on your screen.',
    deleteAccountHeading: 'If you delete your account',
    deleteAccountP1:
      'Your schedule, scores, drill history, devices and tokens are deleted immediately, and the vault on your device is wiped. Your export file keeps working — it is encrypted with your vault key and does not depend on us existing.',
  },
  auth: {
    headline: 'Practise finding your backup, before you need to.',
    lead:
      'Most crypto is not lost to hackers. It is lost to a drawer nobody remembers, a house that got moved out of, and a phrase written down once and never looked at again.',
    bullet1:
      'We never see your seed phrase. There is no field for one and no way to send us one.',
    bullet2:
      'Your hints are encrypted on your device with a key we generate and cannot reset. It is not a wallet phrase and cannot touch your crypto.',
    bullet3:
      'Our servers hold a schedule, a score, and nothing you would mind us reading.',
    signInButton: 'Continue with Google',
    signingIn: 'Signing in…',
    error: 'Sign-in did not complete.',
    emailDisclaimer:
      'Used only to know where to send reminders. Your email never touches the vault.',
  },
  dashboard: {
    title: 'Readiness',
    nothingTracked: 'Nothing set up yet.',
    trackedCount: (count: number) => `${count} backup${count === 1 ? '' : 's'} tracked.`,
    drillDueTitleSingle: 'A drill is due',
    drillDueTitleMultiple: (count: number) => `${count} drills are due`,
    drillDueDesc:
      'About two minutes. Doing it now is worth more than doing it perfectly later.',
    startDrillCta: 'Start the drill',
    locked: 'Locked',
    unreadable: 'Unreadable on this device',
    emptyTitle: 'Nothing to rehearse yet',
    emptyBody:
      'Add a backup and this app will ask you to find it — on a schedule that stretches out while you keep succeeding and tightens the moment you do not.',
    emptyCta: 'Add a backup',
  },
  readinessBands: {
    ready: 'Ready',
    due_soon: 'Due soon',
    at_risk: 'At risk',
    action_required: 'Action required',
    meanings: {
      ready: 'You found it unaided and recently. Nothing to do.',
      due_soon: 'Still solid, but the next drill is worth doing on time.',
      at_risk:
        'You needed help last time. A short drill now is worth more than a long one later.',
      action_required: 'Something needs a decision — not just another reminder.',
    },
  },
  persistence: {
    noBackupTitle: 'No backup file yet',
    noBackupBody:
      'Your hints exist on this device and nowhere else. If the browser clears its storage — and browsers do — the export file is what gets them back.',
    noBackupCta: 'Download it now',
    installTitle: 'Add it to your Home Screen',
    installBody:
      'Installed apps are exempt from the storage cleanup that clears ordinary tabs after a week of not visiting.',
    installCta: 'Install',
    notPersistentTitle: 'Storage is not marked persistent',
    notPersistentBody:
      'This browser has not granted persistent storage yet. Keep using the app and it usually will. Your export file covers you either way.',
  },
  setup: {
    explainTitle: 'First, a key for your hints',
    explainBody:
      'Everything you write here — what the backup is called, what it is written on, the five hints, where exactly it is — is encrypted on this device before it is stored. That encryption needs a key, and in a moment this app is going to generate one for you: six words, 77 bits.',
    notWalletTitle: 'This is not a wallet recovery phrase.',
    notWalletBody:
      'It cannot access any cryptocurrency, restore any wallet, or derive any private key. It unlocks the hints on this device and nothing else.',
    notWalletSub:
      'The words come from a list with every BIP-39 wallet word removed, so your vault key and your wallet phrase can never share a single word — even on the same sheet of paper.',
    factAppTitle: 'Why the app picks it, not you',
    factAppBody:
      'A key you choose is the weakest part of any local encryption, and this one protects every hint you will ever write. So the app generates it and never sees it again.',
    factResetTitle: 'Why we cannot reset it',
    factResetBody:
      'There is nothing on our servers to reset it against. We do not have your hints, so we could not decrypt them for you even under a court order. That is the trade this whole product is built on.',
    factLossTitle: 'What happens if you lose it',
    factLossBody:
      'The hints stay encrypted forever, and you fall back on remembering where your backup is — which is the situation you downloaded this app to avoid. Your actual crypto is untouched either way.',
    factKeepTitle: 'Where to keep it',
    factKeepBody:
      'On paper, with your backup, labelled. Not in a password manager on this same phone — if the phone is what you lose, you lose both at once.',
    generateKeyCta: 'Generate my vault key',
    showKeyTitle: 'Your vault key',
    showKeyBody:
      'Six words. Write them on paper — not in a note on this same phone, because if the phone goes, they both go.',
    vaultKeyLabel: 'Vault key',
    showKeyInstruction:
      'Write VAULT KEY next to it on the paper. If you keep this with your wallet backup — and you should — the label is what stops you confusing the two in five years.',
    showKeyWarning: 'This screen will not be shown again. We store nothing that could reproduce it.',
    writtenDownCta: 'I have written it down',
    confirmTitle: 'Type it back',
    confirmBody:
      'From the paper, not from memory. This is the only way either of us finds out whether it actually got written down.',
    mismatchWarning: 'That is not quite it. Check against the paper.',
    creatingVault: 'Creating the vault…',
    createVaultCta: 'Create the vault',
    showAgainCta: 'Show me the words again',
    exportTitle: 'Save the backup file',
    exportP1:
      'This file plus your vault key restores everything onto a new phone. The file on its own is useless to anyone — it holds no names, no hints, nothing readable.',
    exportP2:
      'Browsers do clear local storage, and phones do get lost. This is the part that survives both, so it is not optional.',
    downloadAgain: 'Download it again',
    downloadFile: 'Download the backup file',
    continueCta: 'Continue',
    downloadFirstCta: 'Download the file to continue',
  },
  newAnchor: {
    title: 'Add a backup',
    lead:
      'None of this leaves your phone. Write it the way you would want to read it in five years, when you have forgotten writing it.',
    nameLabel: 'What do you call it?',
    namePlaceholder: 'the steel plate',
    mediumLabel: 'What is it written on?',
    mediumPlaceholder: 'stamped steel plate',
    mediumHint: 'The medium, not the contents.',
    ladderLegend: 'The hint ladder',
    ladderDesc:
      'Each level gives away more than the last. Finding it without help scores highest, so the shallow levels should be genuinely unhelpful.',
    ladderPrompts: [
      ['Level 1 — almost no help', 'A stranger should learn nothing. "In the house."'],
      ['Level 2 — narrows it down', 'A room, a floor, a category of place.'],
      ['Level 3 — a real clue', 'The piece of furniture, the wall, the container.'],
      ['Level 4 — nearly there', 'Which drawer, which shelf, which side.'],
      ['Level 5 — gets you there', 'What you would want to read on your worst day.'],
    ],
    questionLegend: 'One question, in sequence',
    questionDesc:
      'Answered before any hint is offered. It measures whether the memory is still there at all.',
    questionLabel: 'Question',
    questionPlaceholder: 'Which floor is it on?',
    answerLabel: 'The right answer',
    answerPlaceholder: 'ground floor',
    decoyALabel: 'A plausible wrong answer',
    decoyAPlaceholder: 'first floor',
    decoyAHint: 'Options are shuffled, so position never gives it away.',
    decoyBLabel: 'Another plausible wrong answer',
    decoyBPlaceholder: 'the attic',
    locationLabel: 'The exact location',
    locationHint:
      'Shown only after re-entering your vault key, and hidden again after ten seconds.',
    cadenceQuestion: 'How often, to start with?',
    cadenceExplainer: (max: number, min: number) =>
      `The first drill is a week from now whatever you pick. After that the interval stretches by a quarter each time you find it unaided, up to ${max} days — and drops back to ${min} the first time you cannot.`,
    addingButton: 'Adding…',
    addButton: 'Add this backup',
    codeTitle: 'Write this on the paper',
    codeDesc: 'The same sheet as your vault key, kept with the backup itself.',
    codeP1:
      'During a drill, reading this code back proves you are actually holding the backup rather than remembering where it is. That is the difference between the highest score and the second highest.',
    codeP2: 'We store only a salted hash of it. This screen cannot be shown again, by you or by us.',
    codeAck: 'I have written the code on the paper with the backup.',
    codeDone: 'Done',
  },
  anchorDetail: {
    notFound: 'That backup is not here',
    backButton: 'Back to readiness',
    locked: 'Locked',
    unreadable: 'Unreadable on this device',
    clockRecall: 'Recall',
    clockPhysical: 'Physical',
    everyNDays: (days: number) => `every ${days} days`,
    neverVerified: 'never verified',
    lastSeenDays: (days: number) => `last seen ${days}d ago`,
    capWarning:
      'Readiness is capped at 60 until you physically check this backup, however well recall is going.',
    scheduleHeading: 'Schedule',
    rehearseFrequency: 'How often to rehearse',
    rehearseDesc: (max: number, min: number) =>
      `This is a floor, not a fixture — succeeding unaided stretches it, up to ${max} days. Failing drops it to ${min}.`,
    remindersPaused: 'Reminders are paused',
    pauseReminders: 'Pause reminders',
    pausedDesc: 'Nothing is scheduled. Your scores stop moving too.',
    activeDesc: 'Stops the schedule without deleting anything.',
    resumeButton: 'Resume',
    pauseButton: 'Pause',
    codeHeading: 'Recovery code',
    issueNewCode: 'Issue a new code',
    issueNewCodeDesc:
      'Use this if the paper was lost, seen by someone else, or has become unreadable. The old code stops working immediately.',
    rotateButton: 'Rotate',
    dangerHeading: 'Danger',
    deleteBackup: 'Delete this backup',
    deleteBackupDesc:
      'Removes its schedule, its scores, its drill history, and its hints from this device. The physical backup is untouched.',
    deleteButton: 'Delete',
    rotateSheetTitleNew: 'Write this on the paper',
    rotateSheetTitleConfirm: 'Issue a new recovery code?',
    rotateSheetCrossOut:
      'Cross out the old one. This screen cannot be shown again — we store only a salted hash.',
    rotateSheetWarning:
      'Until you verify this new code in a drill, the backup counts as physically unproven, so the 180-day clock has been reset to due now.',
    rotateSheetWritten: 'I have written it down',
    rotateSheetConfirmBody:
      'The current code stops working the moment a new one is issued, and the physical check will show as due until you verify the new one. Only do this if the old paper is gone or compromised.',
    rotateSheetIssuing: 'Issuing…',
    rotateSheetIssueCta: 'Issue a new code',
    deleteSheetTitle: 'Delete this backup?',
    deleteSheetBody:
      'The hints, the schedule and the history go. Your actual backup — the plate, the paper, whatever it is — is not touched, and neither is anything else in your vault.',
    deleteSheetConfirm: 'Delete it',
    deleteSheetKeep: 'Keep it',
    nowEveryDays: (days: number) => `Now every ${days} days.`,
    scheduleError: 'Could not change the schedule.',
    pausedNotice: 'Reminders paused.',
    resumedNotice: 'Reminders resumed.',
    statusError: 'Could not change that.',
    newCodeError: 'Could not issue a new code.',
    deleteError: 'Could not delete that.',
  },
  drill: {
    notFoundTitle: 'That drill is not here',
    notFoundBody: 'It may already be finished, or it belongs to another account.',
    unlockTitle: 'Unlock to run the drill',
    unlockBody:
      'The hints are encrypted on this device. Nothing about them is on our servers.',
    tagPhysical: 'Physical check',
    tagRecall: 'Recall drill',
    questionDesc:
      'Answer from memory. No hints yet — this is the part that measures whether the memory is still there.',
    searchTitle: 'Now go and find it',
    searchBody:
      'Actually walk over to it. Finding it without a hint is worth more than any answer on this screen.',
    hintLabel: (level: number) => `Hint ${level}`,
    showHintButton: (level: number, deep: boolean) =>
      `Show hint ${level}${deep ? ' — needs your vault key' : ''}`,
    needsVaultKey: 'needs your vault key',
    foundItButton: 'I found it',
    couldNotFindButton: 'I could not find it',
    notRightNowButton: 'Not right now',
    verifyTitle: 'Are you holding it?',
    verifyBody:
      'Reading the recovery code off the paper is the only thing that proves the backup still physically exists. Everything else is a report.',
    verifyCodeTitle: 'I have it in my hand — I will read the code',
    verifyCodeDetail: 'Highest confidence.',
    verifySelfTitle: 'I have it in my hand, but not the code',
    verifySelfDetail: 'Counts as a physical check.',
    verifyMemoryTitle: 'I know where it is, but I did not go and look',
    verifyMemoryDetail: 'Does not reset the 180-day physical clock.',
    timeQuestion: 'How long did it take?',
    timeUnder5: 'Under 5 minutes',
    timeOver5: 'Longer than that',
    conditionTitle: 'What kind of shape is it in?',
    conditionBody:
      'Paper fades, ink runs, steel corrodes. A backup you cannot read is a backup you do not have.',
    conditionIntactTitle: 'Perfect',
    conditionIntactDetail: 'Fully legible, no damage.',
    conditionMinorWearTitle: 'Some wear',
    conditionMinorWearDetail: 'Readable, but showing its age.',
    conditionDegradedTitle: 'Hard to read',
    conditionDegradedDetail: 'Parts are becoming unclear.',
    conditionCompromisedTitle: 'Damaged or missing',
    conditionCompromisedDetail: 'This needs dealing with now.',
    doneTitle: 'Recorded',
    doneReadiness: (score: number, days: number) =>
      `Readiness is now ${score}. The next drill is in ${days} days.`,
    doneDowngraded:
      'The recovery code did not match, so this counts as “held it, did not check the code” rather than a full verification. If you are sure you read it correctly, the code on the paper may be from before a rotation.',
    snoozeDecisionTitle: 'This one needs a decision',
    snoozeDecisionBody:
      'That is the third time this drill has been put off. It is now flagged as needing action rather than another reminder — either run it, or change the schedule to something you will actually keep.',
    snoozeDecisionChange: 'Change the schedule',
    snoozeDecisionNotNow: 'Not now',
    snoozeWhenTitle: 'When instead?',
    snoozeWhenBody:
      'This does not count against you. It counts against the schedule — and after the third time, we will stop asking and flag it instead.',
    snoozeCountText: (count: number) => `Snoozed ${count} of 3 times.`,
    gateReason: (level: number) =>
      `Hint ${level} is close enough to the answer that it needs your vault key again. It will hide itself after ten seconds.`,
    errorSubmit: 'Could not record that. Try again.',
    errorSnooze: 'Could not reschedule that.',
  },
  unlock: {
    title: 'Unlock',
    subtitle: 'Your hints are encrypted here on the device.',
    noVaultTitle: 'No vault on this device',
    noVaultBody: 'Either set one up, or restore from your export file and vault key.',
    setupCta: 'Set up a vault',
    restoreCta: 'Restore from a file',
    biometricButton: 'Use biometrics',
    biometricError: 'That did not work. Use your vault key.',
    keyPlaceholder: 'six words from your paper',
    wrongKeyError: 'That vault key does not open this vault.',
    unlocking: 'Unlocking…',
    unlockButton: 'Unlock',
    restoreAlternative: 'Restore from an export file instead',
  },
  restore: {
    title: 'Restore your vault',
    subtitle: 'Your export file and your vault key. Nothing else, and nothing from us.',
    chooseDifferent: 'Choose a different file',
    chooseFile: 'Choose your export file',
    previewSummary: (count: number, date: string) =>
      `${count} backup${count === 1 ? '' : 's'}, exported ${date}`,
    keyPlaceholder: 'six words from your paper',
    restoring: 'Restoring…',
    restoreButton: 'Restore',
    readError: 'Could not read that file.',
    restoreError: 'Could not restore from that file.',
  },
  settings: {
    title: 'Settings',
    languageHeading: 'Language',
    languageRowTitle: 'Display language',
    languageRowDetail: 'Choose English or 한국어.',
    durabilityHeading: 'Durability',
    backupFileTitle: 'Backup file',
    backupFileSaved: (date: string) => `Last saved ${date}.`,
    backupFileNever: 'Never saved. This is the only copy that survives a lost phone.',
    saveAgain: 'Save again',
    saveNow: 'Save now',
    restoreTitle: 'Restore on another device',
    restoreDetail: 'The file plus your vault key. There is nothing for us to send you.',
    restoreButton: 'Restore',
    deviceHeading: 'This device',
    biometricTitle: 'Biometric unlock',
    biometricEnabled: 'Enabled. Opens hints 1 to 3; deeper hints always need the vault key.',
    biometricAvailable: 'Opens hints 1 to 3 without typing the vault key.',
    biometricUnavailable: 'Not available in this browser.',
    turnOff: 'Turn off',
    enable: 'Enable',
    lockTitle: 'Lock now',
    lockExpires: (time: string) => `The session cache expires at ${time}.`,
    lockLocked: 'The vault is locked.',
    lockButton: 'Lock',
    remindersHeading: 'Reminders',
    pushTitle: 'Push notifications',
    pushGranted: 'On. The notification says “Recovery drill due” and nothing else.',
    pushDenied: 'Blocked in your browser settings. We will email you instead.',
    pushUnsupported: 'Not available in this browser. We will email you instead.',
    pushDefault: 'A lock-screen reminder that names no backup and shows no score.',
    pushOn: 'On',
    pushTurnOn: 'Turn on',
    calendarTitle: 'Calendar subscription',
    calendarDetail:
      'Every event is titled “Readiness check”. It says nothing about which backup, or how you are doing.',
    calendarRotateAgain: 'Rotate again',
    calendarGetLink: 'Get the link',
    calendarOnceNote: 'Shown once. Rotating replaces it and revokes the old one immediately.',
    accountHeading: 'Account',
    signedInAs: 'Signed in as',
    signOut: 'Sign out',
    deleteEverything: 'Delete everything',
    deleteEverythingDetail:
      'Your schedule, your scores, and the vault on this device. Immediate and irreversible.',
    deleteButton: 'Delete',
    deleteSheetTitle: 'Delete everything?',
    deleteSheetBody:
      'This removes your schedule and scores from our servers and wipes the vault on this device. Your export file, if you still have one, keeps working — it is encrypted with your vault key and does not depend on us.',
    deletePlaceholder: 'type DELETE',
    deleteConfirmKeyword: 'DELETE',
    deletingState: 'Deleting…',
    deleteAction: 'Delete everything',
    notices: {
      backupSaved: 'Backup file saved.',
      biometricEnabled: 'Biometric unlock enabled for hints 1 to 3.',
      biometricFailed: 'This device could not provide a biometric key.',
      pushEnabled: 'Push reminders are on.',
      pushFailed: 'Push could not be enabled.',
    },
  },
  guards: {
    vaultKeyBlockedTitle: 'That is your wallet recovery phrase',
    vaultKeyBlockedP1:
      'What you entered is a wallet seed phrase — the words that control your actual crypto. This app never wants it, has nowhere to put it, and has just cleared the field. Nothing was saved and nothing was sent.',
    vaultKeyBlockedP2:
      'The box is asking for something different: your vault key, the six words this app generated when you set it up. It only unlocks the hints stored on this device. It cannot touch any wallet, and no wallet phrase will ever open it.',
    vaultKeyBlockedP3:
      'If you wrote both on the same piece of paper, the vault key is the one whose words are not from a wallet wordlist — that is deliberate, so the two can always be told apart.',
    vaultKeyUnknownWord: (word: string) => `“${word}” is not one of the words this app uses.`,
    vaultKeyDidYouMean: (suggestions: string) => ` Did you mean ${suggestions}?`,
    vaultKeyRemainingWords: (count: number) => `${count} more words.`,
    guardedRunWarning: (count: number) =>
      `${count} words in a row are from a seed-phrase wordlist. A hint should point at your backup, not contain it.`,
    guardedBlockTitles: {
      validChecksum: 'That looked like a recovery phrase',
      consecutiveRun: 'That looked like a recovery phrase',
      pasteVolume: 'That paste looked like a recovery phrase',
    },
    guardedBlockBodies: {
      validChecksum:
        'What you entered was a valid BIP-39 seed phrase. This app never stores seed phrases — there is no field here that wants one, and no way for us to receive one. The field has been cleared and nothing was saved.',
      consecutiveRun:
        'Six or more words in a row came from a seed-phrase wordlist. The field has been cleared and nothing was saved. Write a hint that would remind you where the backup is, not what it says.',
      pasteVolume:
        'The text you pasted contained several seed-phrase words. The field has been cleared and nothing was saved. Hints should point at the backup, never contain it.',
    },
  },
};
